import React, { useState, useEffect } from 'react';
import { Settings, Image as ImageIcon, RefreshCw, CheckCircle, AlertCircle, BarChart3, FileText, Lock, Unlock } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';
import GoogleCalendarSettings from './GoogleCalendarSettings';

const AdminSettings = () => {
  const { adminToken } = useAdminAuth();
  const { toast } = useToast();
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [converting, setConverting] = useState(false);
  const [conversionResult, setConversionResult] = useState(null);
  const [error, setError] = useState(null);
  const [listinoIsPublic, setListinoIsPublic] = useState(false);
  const [listinoLoading, setListinoLoading] = useState(false);

  // Carica stato visibilità listino
  const loadListinoVisibility = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/listino-visibility`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setListinoIsPublic(data.is_public);
      }
    } catch (err) {
      console.error('Errore caricamento visibilità listino:', err);
    }
  };

  // Toggle visibilità listino
  const toggleListinoVisibility = async () => {
    setListinoLoading(true);
    
    try {
      const newValue = !listinoIsPublic;
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/listino-visibility?is_public=${newValue}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setListinoIsPublic(newValue);
        toast({
          title: "Impostazione aggiornata",
          description: data.message
        });
      } else {
        throw new Error('Errore aggiornamento impostazione');
      }
    } catch (err) {
      toast({
        title: "Errore",
        description: "Impossibile aggiornare l'impostazione",
        variant: "destructive"
      });
    } finally {
      setListinoLoading(false);
    }
  };

  // Carica statistiche immagini
  const loadStatistics = async () => {
    if (!adminToken) {
      setError('Token admin non disponibile');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/image-statistics`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Errore nel caricamento delle statistiche');
      }
      
      const data = await response.json();
      setStatistics(data);
    } catch (err) {
      setError(err.message);
      console.error('Errore caricamento statistiche:', err);
    } finally {
      setLoading(false);
    }
  };

  // Converti immagini in WebP
  const convertImagesToWebP = async () => {
    if (!window.confirm('Sei sicuro di voler convertire tutte le immagini in WebP? Questa operazione potrebbe richiedere alcuni minuti.')) {
      return;
    }

    setConverting(true);
    setError(null);
    setConversionResult(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/convert-images-to-webp?quality=85`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la conversione');
      }

      const data = await response.json();
      setConversionResult(data);
      
      // Ricarica le statistiche
      await loadStatistics();
      
    } catch (err) {
      setError(err.message);
    } finally {
      setConverting(false);
    }
  };

  useEffect(() => {
    loadStatistics();
    loadListinoVisibility();
  }, []);

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Settings className="w-8 h-8 text-purple-600" />
            <h1 className="text-3xl font-bold text-gray-900">Impostazioni</h1>
          </div>
          <p className="text-gray-600">Gestisci le impostazioni e l'ottimizzazione del sistema</p>
        </div>

        {/* Sezione Listino Prezzi */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Visibilità Listino Prezzi</h2>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
            <p className="text-sm text-blue-800">
              <strong>Attenzione:</strong> Quando il listino è pubblico, tutti gli utenti (anche non registrati) possono visualizzarlo. 
              Quando è privato, solo gli utenti registrati e loggati possono accedervi.
            </p>
          </div>

          <div className="flex items-center justify-between p-6 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-4">
              {listinoIsPublic ? (
                <Unlock className="w-8 h-8 text-green-600" />
              ) : (
                <Lock className="w-8 h-8 text-amber-600" />
              )}
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Stato: {listinoIsPublic ? 'Pubblico' : 'Privato (Richiede Login)'}
                </h3>
                <p className="text-sm text-gray-600">
                  {listinoIsPublic 
                    ? 'Il listino prezzi è visibile a tutti senza login'
                    : 'Solo utenti registrati possono vedere il listino prezzi'}
                </p>
              </div>
            </div>
            
            <button
              onClick={toggleListinoVisibility}
              disabled={listinoLoading}
              className={`relative inline-flex h-10 w-20 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                listinoIsPublic ? 'bg-green-600' : 'bg-gray-300'
              } ${listinoLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-8 w-8 transform rounded-full bg-white transition-transform ${
                  listinoIsPublic ? 'translate-x-11' : 'translate-x-1'
                }`}
              />
              {listinoLoading && (
                <RefreshCw className="absolute inset-0 m-auto w-4 h-4 animate-spin text-white" />
              )}
            </button>
          </div>
        </div>

        {/* Sezione Ottimizzazione Immagini */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <ImageIcon className="w-6 h-6 text-purple-600" />
            <h2 className="text-2xl font-bold text-gray-900">Ottimizzazione Immagini WebP</h2>
          </div>

          {/* Statistiche */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
              <span className="ml-3 text-gray-600">Caricamento statistiche...</span>
            </div>
          ) : statistics ? (
            <>
              {/* Statistiche Generali */}
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <BarChart3 className="w-8 h-8 text-purple-600" />
                    <span className="text-3xl font-bold text-purple-900">
                      {statistics.total_images}
                    </span>
                  </div>
                  <p className="text-sm text-purple-700 font-medium">Totale Immagini</p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                    <span className="text-3xl font-bold text-green-900">
                      {statistics.total_webp}
                    </span>
                  </div>
                  <p className="text-sm text-green-700 font-medium">Già in WebP</p>
                </div>

                <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <AlertCircle className="w-8 h-8 text-amber-600" />
                    <span className="text-3xl font-bold text-amber-900">
                      {statistics.total_to_convert}
                    </span>
                  </div>
                  <p className="text-sm text-amber-700 font-medium">Da Convertire</p>
                </div>
              </div>

              {/* Dettagli per categoria */}
              <div className="space-y-4 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Dettagli per Categoria</h3>
                
                {/* Liquori */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-800">🍷 Liquori</h4>
                    <span className="text-sm text-gray-600">
                      {statistics.statistics.liquors.total} totali
                    </span>
                  </div>
                  <div className="flex gap-4 text-sm">
                    <span className="text-green-600">
                      ✓ {statistics.statistics.liquors.webp} WebP
                    </span>
                    <span className="text-amber-600">
                      → {statistics.statistics.liquors.other} da convertire
                    </span>
                  </div>
                  {statistics.statistics.liquors.total > 0 && (
                    <div className="mt-2 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full transition-all"
                        style={{ width: `${(statistics.statistics.liquors.webp / statistics.statistics.liquors.total) * 100}%` }}
                      ></div>
                    </div>
                  )}
                </div>

                {/* Ingredienti */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-800">🌿 Ingredienti</h4>
                    <span className="text-sm text-gray-600">
                      {statistics.statistics.ingredients.total} totali
                    </span>
                  </div>
                  <div className="flex gap-4 text-sm">
                    <span className="text-green-600">
                      ✓ {statistics.statistics.ingredients.webp} WebP
                    </span>
                    <span className="text-amber-600">
                      → {statistics.statistics.ingredients.other} da convertire
                    </span>
                  </div>
                  {statistics.statistics.ingredients.total > 0 && (
                    <div className="mt-2 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full transition-all"
                        style={{ width: `${(statistics.statistics.ingredients.webp / statistics.statistics.ingredients.total) * 100}%` }}
                      ></div>
                    </div>
                  )}
                </div>

                {/* Bottiglie */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-800">🍾 Bottiglie</h4>
                    <span className="text-sm text-gray-600">
                      {statistics.statistics.bottles.total} totali
                    </span>
                  </div>
                  <div className="flex gap-4 text-sm">
                    <span className="text-green-600">
                      ✓ {statistics.statistics.bottles.webp} WebP
                    </span>
                    <span className="text-amber-600">
                      → {statistics.statistics.bottles.other} da convertire
                    </span>
                  </div>
                  {statistics.statistics.bottles.total > 0 && (
                    <div className="mt-2 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full transition-all"
                        style={{ width: `${(statistics.statistics.bottles.webp / statistics.statistics.bottles.total) * 100}%` }}
                      ></div>
                    </div>
                  )}
                </div>
              </div>

              {/* Pulsante Conversione */}
              <div className="flex items-center gap-4">
                <button
                  onClick={convertImagesToWebP}
                  disabled={converting || statistics.total_to_convert === 0}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                    converting || statistics.total_to_convert === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-purple-600 hover:bg-purple-700 text-white shadow-lg hover:shadow-xl'
                  }`}
                >
                  {converting ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      Conversione in corso...
                    </>
                  ) : (
                    <>
                      <ImageIcon className="w-5 h-5" />
                      Converti {statistics.total_to_convert} Immagini in WebP
                    </>
                  )}
                </button>

                <button
                  onClick={loadStatistics}
                  disabled={loading || converting}
                  className="flex items-center gap-2 px-4 py-3 rounded-lg font-semibold border-2 border-gray-300 text-gray-700 hover:bg-gray-50 transition-all"
                >
                  <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                  Aggiorna
                </button>
              </div>
            </>
          ) : null}

          {/* Errore */}
          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-red-800">
                <AlertCircle className="w-5 h-5" />
                <span className="font-semibold">Errore:</span>
              </div>
              <p className="mt-2 text-red-700">{error}</p>
            </div>
          )}

          {/* Risultato Conversione */}
          {conversionResult && (
            <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-center gap-2 text-green-800 mb-4">
                <CheckCircle className="w-6 h-6" />
                <span className="text-lg font-bold">Conversione Completata!</span>
              </div>
              
              <div className="grid md:grid-cols-4 gap-4 mb-4">
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">{conversionResult.results.converted}</p>
                  <p className="text-sm text-gray-600">Convertite</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-gray-600">{conversionResult.results.skipped}</p>
                  <p className="text-sm text-gray-600">Saltate</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-red-600">{conversionResult.results.failed}</p>
                  <p className="text-sm text-gray-600">Fallite</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-purple-600">{conversionResult.results.total}</p>
                  <p className="text-sm text-gray-600">Totali</p>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-sm text-gray-700">
                  <strong>Liquori:</strong> {conversionResult.results.details.liquors.converted} convertiti
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Ingredienti:</strong> {conversionResult.results.details.ingredients.converted} convertiti
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Bottiglie:</strong> {conversionResult.results.details.bottles.converted} convertite
                </p>
              </div>

              {conversionResult.results.errors.length > 0 && (
                <div className="mt-4 p-3 bg-red-50 rounded-lg">
                  <p className="text-sm font-semibold text-red-800 mb-2">Errori riscontrati:</p>
                  <ul className="text-xs text-red-700 space-y-1">
                    {conversionResult.results.errors.slice(0, 5).map((error, idx) => (
                      <li key={idx}>• {error}</li>
                    ))}
                    {conversionResult.results.errors.length > 5 && (
                      <li className="font-semibold">... e altri {conversionResult.results.errors.length - 5} errori</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Google Calendar Integration */}
          <div className="mt-6">
            <GoogleCalendarSettings />
          </div>

          {/* Info Box */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">ℹ️ Informazioni</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Le immagini verranno convertite in formato WebP con qualità 85%</li>
              <li>• Le immagini originali verranno salvate come backup nel database</li>
              <li>• I file WebP verranno caricati su S3 nella cartella "optimized/"</li>
              <li>• Il processo può richiedere diversi minuti per molte immagini</li>
              <li>• Le immagini già in formato WebP verranno saltate</li>
            </ul>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
